'use client'

/**
 * Раздел «Решения» — модели работы бизнеса с клиентом.
 * Здесь живут только общие для раздела данные: навигация, SEO,
 * состав решения и релевантные кейсы. Смысловой контент — внутри страниц.
 */

export interface SolutionKitItem {
  label: string;
  to: string;
}

export interface SolutionMeta {
  id: string;
  path: string;
  nav: string;
  /** короткая подпись в меню и на /solutions */
  hint: string;
  /** одна строка сути сценария */
  essence: string;
  marker: string;
  title: string;
  description: string;
  h1: string;
  /** состав решения — у каждого сценария свой */
  kit: SolutionKitItem[];
  caseSlugs: string[];
  related: string[];
}

export const SOLUTIONS: SolutionMeta[] = [
  {
    id: "services",
    path: "/platform/solutions/services",
    nav: "Услуги и сервис",
    hint: "Запись, визит, повторная работа",
    essence: "Весь путь клиента — от первого обращения до повторной записи.",
    marker: "01",
    title: "Решение для сервисного бизнеса — от записи до следующего визита | VCENTRE.ONE",
    description:
      "Запись, CRM, личный кабинет, напоминания, оплата и повторная работа с клиентом в одном процессе. Решение VCENTRE.ONE для клиник, салонов и сервисных компаний.",
    h1: "Весь путь клиента — от первого обращения до повторной записи",
    kit: [
      { label: "CRM и клиенты", to: "/platform/crm" },
      { label: "Приложение компании", to: "/platform/app" },
      { label: "Сайт", to: "/platform/websites" },
      { label: "Чаты и напоминания", to: "/platform/communications" },
      { label: "Автоматизация", to: "/platform/automation-ai" },
      { label: "Лояльность", to: "/platform/app" },
    ],
    caseSlugs: ["private-clinic", "groove"],
    related: ["commerce", "b2b", "online-offline"],
  },
  {
    id: "commerce",
    path: "/platform/solutions/commerce",
    nav: "Торговля и e-commerce",
    hint: "Каталог, заказ, лояльность, возврат",
    essence: "Продажа не заканчивается после кнопки «Оплатить».",
    marker: "02",
    title: "Решение для торговли и e-commerce — свой канал повторных продаж | VCENTRE.ONE",
    description:
      "Каталог, заказ, оплата, CRM, лояльность и повторные коммуникации как один клиентский процесс. Собственное приложение и сайт вместо чужой клиентской базы.",
    h1: "Продажа не заканчивается после кнопки «Оплатить»",
    kit: [
      { label: "Каталог и сайт", to: "/platform/websites" },
      { label: "Приложение компании", to: "/platform/app" },
      { label: "CRM и заказы", to: "/platform/crm" },
      { label: "Рассылки и push", to: "/platform/communications" },
      { label: "Интеграции и 1С", to: "/platform/integrations" },
      { label: "Лояльность и рефералы", to: "/platform/app" },
    ],
    caseSlugs: ["groverman", "piter"],
    related: ["services", "online-offline", "education"],
  },
  {
    id: "b2b",
    path: "/platform/solutions/b2b",
    nav: "B2B и продажи",
    hint: "Лиды, сделки, менеджеры, документы",
    essence: "B2B-продажи без потерянных договорённостей и ручного контроля.",
    marker: "03",
    title: "Решение для B2B и сложных продаж — сделки, менеджеры, документы | VCENTRE.ONE",
    description:
      "Клиенты, компании, сделки, задачи, документы и коммуникации в одном рабочем контуре. Решение VCENTRE.ONE для B2B-продаж, поставщиков и агентств.",
    h1: "B2B-продажи без потерянных договорённостей и ручного контроля",
    kit: [
      { label: "CRM: клиенты и компании", to: "/platform/crm" },
      { label: "Сделки и задачи", to: "/platform/crm" },
      { label: "Роли сотрудников", to: "/platform/crm" },
      { label: "Чаты и переписка", to: "/platform/communications" },
      { label: "Автоматизация этапов", to: "/platform/automation-ai" },
      { label: "Интеграции: 1С, банки", to: "/platform/integrations" },
    ],
    caseSlugs: ["groverman", "dobrodomik"],
    related: ["online-offline", "services", "commerce"],
  },
  {
    id: "events",
    path: "/platform/solutions/events",
    nav: "Мероприятия и сообщества",
    hint: "Регистрация, QR, нетворкинг, сообщество",
    essence: "Участник приходит на событие один раз. Сообщество остаётся надолго.",
    marker: "04",
    title: "Решение для мероприятий и сообществ — регистрация, QR, нетворкинг | VCENTRE.ONE",
    description:
      "Регистрация, билеты и QR, программа, партнёры, знакомства и чаты в приложении мероприятия. Среда, которая работает до, во время и после события.",
    h1: "Участник приходит на событие один раз. Сообщество может остаться с ним надолго",
    kit: [
      { label: "Приложение мероприятия", to: "/platform/app" },
      { label: "Регистрация и сайт", to: "/platform/websites" },
      { label: "Билеты и события", to: "/platform/modules" },
      { label: "Цифровая визитка", to: "/platform/modules" },
      { label: "Чаты и push", to: "/platform/communications" },
      { label: "CRM участников", to: "/platform/crm" },
    ],
    caseSlugs: ["business-forum", "groove"],
    related: ["education", "services", "online-offline"],
  },
  {
    id: "education",
    path: "/platform/solutions/education",
    nav: "Образование, эксперты и подписки",
    hint: "Контент, квизы, доступ, продление",
    essence: "Не просто доступ к материалам, а отношения с учеником.",
    marker: "05",
    title: "Решение для образования, экспертов и подписок — путь участника | VCENTRE.ONE",
    description:
      "Контент, личный кабинет, квизы, AI-функции, коммуникации, подписки и CRM как единый пользовательский сценарий обучения и подписных продуктов.",
    h1: "Не просто выдать доступ к материалам. Построить отношения с учеником",
    kit: [
      { label: "Личный кабинет в приложении", to: "/platform/app" },
      { label: "Онлайн-курсы (интеграция)", to: "/platform/integrations" },
      { label: "Квизы и сценарии", to: "/platform/automation-ai" },
      { label: "AI-функции", to: "/platform/automation-ai" },
      { label: "Чаты и уведомления", to: "/platform/communications" },
      { label: "CRM участников", to: "/platform/crm" },
    ],
    caseSlugs: ["dobrodomik", "piter"],
    related: ["events", "commerce", "services"],
  },
  {
    id: "online-offline",
    path: "/platform/solutions/online-offline",
    nav: "Онлайн + офлайн клиенты",
    hint: "Звонок, офис, менеджер — и цифровой профиль позже",
    essence: "Онлайн- и офлайн-клиенты сходятся в единый профиль.",
    marker: "06",
    title: "Онлайн и офлайн клиенты в одной системе — работа без приложения | VCENTRE.ONE",
    description:
      "Клиент позвонил, пришёл в офис или написал менеджеру — вы уже можете вести его в VCENTRE.ONE. Цифровой профиль подключается позже, история сохраняется.",
    h1: "Клиенту не обязательно быть в приложении, чтобы быть вашим клиентом в системе",
    kit: [
      { label: "CRM и ручное создание клиента", to: "/platform/crm" },
      { label: "Сделки и задачи", to: "/platform/crm" },
      { label: "Чаты и звонки менеджера", to: "/platform/communications" },
      { label: "Приложение — по желанию", to: "/platform/app" },
      { label: "Сайт как точка входа", to: "/platform/websites" },
      { label: "Интеграции с учётом", to: "/platform/integrations" },
    ],
    caseSlugs: ["private-clinic", "groverman"],
    related: ["b2b", "services", "commerce"],
  },
];

export const getSolution = (id: string) => SOLUTIONS.find((s) => s.id === id)!;
